import React from "react";

import CalendarIcon from "@uui/Calendar";
import { isEmpty } from "lodash/fp";

import Typography from "@mui/material/Typography";

import {
  CustomTooltipWrapper,
  SecondaryTextWithIcon,
} from "@app/analytics/Pulse/components/styles";
import type { PulseTopicSession } from "@app/analytics/Pulse/types";
import ROUTES from "@app/common/routes";
import { ELLIPSIS_SX } from "@app/common/styles";

import { Link } from "@rui/Link";
import Stack, { Row } from "@rui/Stack";

const SessionListPopover = ({
  children,
  max = 25,
  sessions,
}: {
  children: React.ReactNode;
  max?: number;
  sessions: PulseTopicSession[];
}) => {
  if (isEmpty(sessions)) return null;

  const visibleSessions = sessions.slice(0, max);
  const additionalSessions = sessions.slice(max);

  return (
    <CustomTooltipWrapper
      title={
        <Stack padding={0.5} sx={{ pointerEvents: "auto" }}>
          {visibleSessions.map((session) => (
            <Row key={session.id} spacing={1}>
              <SecondaryTextWithIcon
                Icon={CalendarIcon}
                sx={{
                  overflow: "visible",
                  fontSize: "0.875rem",
                  fontWeight: 400,
                }}
              >
                {session.meetingStartTime.toLocaleString({
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </SecondaryTextWithIcon>
              <Link
                href={ROUTES.meetingReportById(session.id)}
                sx={{
                  fontSize: "0.875rem",
                  maxWidth: "200px",
                  ...ELLIPSIS_SX,
                }}
              >
                {session.meetingTitle}
              </Link>
            </Row>
          ))}
          {additionalSessions.length > 0 && (
            <Row sx={{ pt: 0.5 }}>
              <Typography
                variant="bodySecondaryMed"
                sx={{
                  color: "shades.neutral.300",
                  fontFamily: (theme) => theme.typography.fontFamilySecondary,
                }}
              >
                + {additionalSessions.length} more
              </Typography>
            </Row>
          )}
        </Stack>
      }
    >
      <Row
        sx={{
          px: "4px",
          ml: "-2px",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: "transparent.black.2",
            cursor: "pointer",
          },
        }}
      >
        {children}
      </Row>
    </CustomTooltipWrapper>
  );
};

export default SessionListPopover;
